import React, { FunctionComponent } from 'react';
import withStyles from 'react-jss';
import { Grid, Header } from 'semantic-ui-react';
import { withRouter } from 'react-router';
import { compose } from 'recompose';
import { Location } from 'history';
import queryString from 'query-string';
import { format } from 'd3-format';
import classnames from 'classnames';
import { pathOr, isEmpty } from 'ramda';

import Query from 'scenes/Report/components/Query';
import ReportBase, { ReportParams } from 'scenes/Report/components/ReportBase';
import { ClassProp } from 'helpers/types';
import Table from 'components/Table';
import { Field } from 'components/DownloadWidget/DownloadWidget';
import { formatDate } from 'helpers/date';

import GET_MERCHANT_MEMBERSHIPS_METRICS from './MerchantMembershipsMetrics.queries';
import styles from './MerchantMembershipsMetrics.styles';

type Props = {
  classes: ClassProp<ReturnType<typeof styles>>;
  location: Location;
};

type Membership = {
  day: string;
  lessThan100dollarMembershipCount: number;
  greaterThan100dollarMembershipCount: number;
  totalMembershipRevenue: number;
  cashbackAmount: number;
  campaignFeeAmount: number;
};

type MerchantMembershipsMetrics = {
  result: {
    totals: Membership[];
    online: Membership[];
    inStore: Membership[];
  };
};

type QueryResponse = {
  merchantMembershipsMetrics: MerchantMembershipsMetrics;
};

type MembershipLevelConfig = {
  [key: string]: {
    smallerMembership: string;
    biggerMembership: string;
  };
};

const membershipLevelConfig: MembershipLevelConfig = {
  [`Sam's Club`]: {
    smallerMembership: '$45 Membership',
    biggerMembership: '$100 Membership',
  },
  ['Costco']: {
    smallerMembership: '$60 Basic',
    biggerMembership: '$120 Executive',
  },
  [`BJ's Wholesale Club`]: {
    smallerMembership: '$55 Basic (inner circle)',
    biggerMembership: '$110 Pro (perks) ',
  },
};

const getTableHeader = (merchantName = '') => [
  {
    source: ['day'],
    sortable: ['day'],
    label: 'Date',
    transform: (date: string) =>
      date !== '-' ? formatDate(date, 'MM/DD/YYYY') : 'Totals',
  },
  {
    source: ['lessThan100dollarMembershipCount'],
    sortable: ['lessThan100dollarMembershipCount'],
    justify: 'right',
    label: pathOr(
      '< $100 membership',
      [merchantName, 'smallerMembership'],
      membershipLevelConfig,
    ),
  },
  {
    source: ['greaterThan100dollarMembershipCount'],
    sortable: ['greaterThan100dollarMembershipCount'],
    justify: 'right',
    label: pathOr(
      '>= $100 membership',
      [merchantName, 'biggerMembership'],
      membershipLevelConfig,
    ),
  },
  {
    source: ['totalMembershipRevenue'],
    sortable: ['totalMembershipRevenue'],
    justify: 'right',
    label: 'Total Revenue',
    transform: format('$,.0f'),
  },
  {
    source: ['cashbackAmount'],
    sortable: ['cashbackAmount'],
    justify: 'right',
    label: 'Cashback Amount',
    transform: format('$,.2f'),
  },
  {
    source: ['campaignFeeAmount'],
    sortable: ['campaignFeeAmount'],
    justify: 'right',
    label: 'Campaign Fee Amount',
    transform: format('$,.2f'),
  },
];

const getCSVFields = (merchantName = '') => [
  {
    label: 'Date',
    value: ({ day }: Membership) =>
      day !== '-' ? formatDate(day, 'MM/DD/YYYY') : 'Totals',
  },
  {
    label: pathOr(
      '< $100 membership',
      [merchantName, 'smallerMembership'],
      membershipLevelConfig,
    ),
    value: 'lessThan100dollarMembershipCount',
  },
  {
    label: pathOr(
      '>= $100 membership',
      [merchantName, 'biggerMembership'],
      membershipLevelConfig,
    ),
    value: 'greaterThan100dollarMembershipCount',
  },
  {
    label: 'Total Revenue',
    value: ({ totalMembershipRevenue }: Membership) =>
      format('$,.2f')(totalMembershipRevenue),
  },
  {
    label: 'Cashback Amount',
    value: ({ cashbackAmount }: Membership) => format('$,.2f')(cashbackAmount),
  },
  {
    label: 'Campaign Fee Amount',
    value: ({ campaignFeeAmount }: Membership) =>
      format('$,.2f')(campaignFeeAmount),
  },
];

const renderTable = (
  tableHeader: any,
  data: Membership[],
  CSVFields: Field[],
) => (
  <Table
    sortDefaultColumn={{ name: 'Date', sortable: ['day'] }}
    tableHeader={tableHeader}
    data={data}
    splitEveryArg={10}
    showTotals
    exportData
    csvFields={CSVFields}
  />
);

const renderReport = (
  params: ReportParams,
  classes: ClassProp<ReturnType<typeof styles>>,
) => ({ merchantMembershipsMetrics: { result } }: QueryResponse) => {
  const { totals, online, inStore } = result;

  const tableHeader = getTableHeader(params.merchantName);
  const CSVFields = getCSVFields(params.merchantName);

  return (
    <ReportBase header="Memberships Metrics" params={params}>
      <Grid.Row className={classes.charts} centered>
        <Grid.Column width={16} className={classes.gridTable}>
          <Header className={classes.tableHeader} as="h3">
            Totals (in store and online)
          </Header>

          <Header className={classes.tableHeader} as="h4">
            All totals include both cleared and pending transactions
          </Header>

          {!isEmpty(totals) && renderTable(tableHeader, totals, CSVFields)}
        </Grid.Column>

        <Grid.Column
          width={16}
          className={classnames(classes.gridTable, 'space')}
        >
          <Header className={classes.tableHeader} as="h3">
            Memberships - In Store
          </Header>

          {!isEmpty(inStore) && renderTable(tableHeader, inStore, CSVFields)}
        </Grid.Column>

        <Grid.Column
          width={16}
          className={classnames(classes.gridTable, 'space')}
        >
          <Header className={classes.tableHeader} as="h3">
            Memberships - Online
          </Header>

          {!isEmpty(online) && renderTable(tableHeader, online, CSVFields)}
        </Grid.Column>
      </Grid.Row>
    </ReportBase>
  );
};

const MerchantMembershipsMetrics: FunctionComponent<Props> = ({
  classes,
  location,
}) => {
  const params = queryString.parse(location.search) as ReportParams;

  return (
    <Query
      query={GET_MERCHANT_MEMBERSHIPS_METRICS}
      reportName="Memberships Metrics"
      queryKey="result"
      propName="merchantMembershipsMetrics"
      checkForValidData={false}
      variables={{
        params,
      }}
      render={renderReport(params, classes)}
    />
  );
};

export default compose(
  withStyles(styles as any),
  withRouter,
)(MerchantMembershipsMetrics as any);
